import { SidenavRow } from '@app/components/layout/sidenav/base';
import {
  getSidenavRailPaddingLeft,
  SIDENAV_TOKENS,
} from '@app/components/layout/sidenav/sidenav-tokens';
import { useSidenavContext } from '@app/components/layout/sidenav/SidenavContext';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { THEME_TOGGLE_TOUR_ELEMENT_ID, useThemeToggleTour } from '../../footer/useThemeToggleTour';
import {
  accountChevronSx,
  accountClientLineSx,
  accountTextColumnSx,
  getAccountRowSx,
  getAccountTriggerSx,
  getAccountUserLineSx,
} from './sidenav-account-row-styles';
import { SidenavAccountPopover } from './SidenavAccountPopover';
import { SidenavUserAvatar } from './SidenavUserAvatar';
import { useSidenavAccountIdentity } from './useSidenavAccountIdentity';

/**
 * Account row: full-width avatar + stacked client/user that opens the account popover.
 * Collapsed: avatar only (text lives in the popover).
 */
export function SidenavAccountRow() {
  // Keep the theme tour mounted on the account trigger (always in the DOM).
  useThemeToggleTour();
  const { collapsed } = useSidenavContext();
  const { avatarSrc, initials, clientLine, displayNameMin } = useSidenavAccountIdentity();
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const avatarSize = SIDENAV_TOKENS.accountAvatarSize;
  // Same centerline as collapsed nav icons / brand mark — does not jump on toggle.
  const avatarPaddingLeft = getSidenavRailPaddingLeft(avatarSize);

  return (
    <>
      <SidenavRow sx={getAccountRowSx(avatarPaddingLeft)}>
        <ButtonBase
          id={THEME_TOGGLE_TOUR_ELEMENT_ID}
          ref={triggerRef}
          onClick={() => setMenuOpen((open) => !open)}
          sx={(theme) => getAccountTriggerSx(theme, { collapsed, menuOpen })}
        >
          <SidenavUserAvatar
            size={avatarSize}
            src={avatarSrc}
            initials={initials}
            initialsSx={{ fontSize: '0.65rem' }}
          />
          {!collapsed && (
            <>
              <Box sx={accountTextColumnSx}>
                <Typography component="div" data-cy="auth-client-name" sx={accountClientLineSx}>
                  {clientLine}
                </Typography>
                <Typography component="div" sx={getAccountUserLineSx()}>
                  {displayNameMin}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon sx={accountChevronSx} />
            </>
          )}
        </ButtonBase>
      </SidenavRow>

      <SidenavAccountPopover
        open={menuOpen}
        anchorEl={triggerRef.current}
        onClose={() => setMenuOpen(false)}
      />
    </>
  );
}
